import { setUser } from '@sentry/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { userService } from '@src/api/services';
import { queryClientDefaultOptions } from '@src/configs/tanstack.configs';
import { IS_DEVELOPMENT, QUERY_KEYS, STORAGE_KEY } from '@src/constants';
import { USER_ROLES } from '@src/constants/user.constants';
import { getToken } from '@src/helpers/axios.helpers';
import { checkUserRole } from '@src/helpers/profile.helpers';
import { getDataLocaleWithEncryption } from '@src/helpers/storage.helpers';
import { IUser, IUserRole } from '@src/interfaces/user.interfaces';

interface IUseUserInfo {
  onSuccess?: (user: IUser) => void;
  onError?: () => void;
  onFinally?: () => void;
  enabled?: boolean;
  refetchOnWindowFocus?: boolean | 'always';
}

export const useUserInfo = ({
  onSuccess,
  onError,
  onFinally,
  enabled = true,
  refetchOnWindowFocus,
}: IUseUserInfo = {}) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [QUERY_KEYS.authUserInfo],
    queryFn: async () => {
      const response = await userService.aboutMe();

      const user = response.data.data;

      if (user) {
        queryClient.setDefaultOptions({
          ...queryClientDefaultOptions,
          queries: {
            ...queryClientDefaultOptions.queries,
            enabled: true,
          },
        });
      }

      setUser(user);

      onSuccess?.(user);
      onFinally?.();

      await checkUserRole(user.role);

      return user;
    },
    throwOnError: () => {
      onError?.();
      onFinally?.();
      return false;
    },
    retry: false,
    enabled: !!getToken() && enabled,
    refetchOnWindowFocus,
  });
};

export const useUserTester = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.userTester],
    queryFn: async () => {
      if (IS_DEVELOPMENT) return true;

      const prevRole = await getDataLocaleWithEncryption<IUserRole>(
        STORAGE_KEY.ROLE,
      );
      return prevRole === USER_ROLES.TESTER;
    },
    retry: false,
    enabled: true,
  });
};
